import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import { UserSetting } from '../models/tenant-setting.model';

/**
 * The caller's own settings (base path /api/v1/users/me/setting). The user is
 * resolved from the JWT subject on the backend. Writes are accepted only for
 * user-writable keys (USER_OVERRIDABLE and USER_ONLY); everything else 403s.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/users/me/setting`;

  /** Every registered setting with its effective value and cascade source. */
  getAll(): Observable<ApiResponse<UserSetting[]>> {
    return this.http.get<ApiResponse<UserSetting[]>>(this.baseUrl);
  }

  /** Stores a user override. The body is the raw JSON value. */
  update(key: string, value: unknown): Observable<ApiResponse<null>> {
    // Encode explicitly: HttpClient sends a plain string body verbatim as
    // text/plain, but the API expects a JSON document ('sl-SI' must go out
    // as "sl-SI" with application/json).
    return this.http.put<ApiResponse<null>>(
      `${this.baseUrl}/${encodeURIComponent(key)}`,
      JSON.stringify(value),
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** Removes the user override; the value falls back to tenant, then default. Idempotent. */
  reset(key: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${encodeURIComponent(key)}`);
  }
}
